import React from 'react';
import { StyleSheet } from 'react-native';
import format from 'date-fns/format';
import { DocumentAccessLevel, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, DNATextProps, useTheme, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentThumbnail,
  DNAFileTypeChip,
  DNAPopover,
  DNARowsComponent,
  RenderExcerpt,
} from 'src/components/DNA';
import { DocumentORM } from 'src/types/types';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider.proxy';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';

export type DNADocumentMeetingRowProps = {
  onPin?: () => void,
  isDesktop?: boolean;
  isOnline?: boolean;
}

const styles = StyleSheet.create({
  containerStyle: {
    backgroundColor: colors['color-white-transparent-6'],
    borderRadius: 4,
    marginVertical: 4,
  },
  titleText: {
    marginLeft: 24,
  },
})

const DNADocumentMeetingRow: DNARowsComponent<
  DocumentORM,
  DNADocumentMeetingRowProps
> = ({
  ORM: documentORM,
  onPress,
  style,
  isOnline,
  isSearch,
}) => {
  const theme = useTheme()
  const isPersonalDocument = documentORM.model.accessLevel === DocumentAccessLevel.USER;
  const { latestUsableDocumentVersionORM } = documentORM.relations.version
  const { meetingORM } = useMeetingsState();
  const isVirtualMeeting = meetingORM?.model.type === 'VIRTUAL'
  const isWebDoc = documentORM.model.type === FileType.WEB
  const isHTMLDoc = documentORM.model.type === FileType.HTML
  const isMultiPageHTML = !!latestUsableDocumentVersionORM.meta.isMultiPageHTML
  const unavailableContent =
    (!isOnline && !latestUsableDocumentVersionORM.meta.assets.isContentCached) ||
    ((isWebDoc) && (isVirtualMeeting || !isOnline)) ||
    (isHTMLDoc && isVirtualMeeting && !isMultiPageHTML)

  const isDisabledHTMLorWebDocument =
    (isWebDoc && isVirtualMeeting) ||
    (isHTMLDoc && isVirtualMeeting && !isMultiPageHTML)

  const isInteractive = latestUsableDocumentVersionORM.meta.permissions.MSLPresent && !unavailableContent
  const title = documentORM.relations.version.latestPublishedDocumentVersionORM?.model.title

  const { documentIdSnippetMap } = useDocumentSearchV2Context();

  const snippet = documentIdSnippetMap.get(documentORM.model.id);

  const showSnippet = snippet?.documentExcerpt && isSearch;

  const titleProps: DNATextProps = {
    b1: true,
    testID: 'meeting-document-gridlist-name',
    numberOfLines: 1,
    style: { color: colors['color-text-basic'] },
  }

  const snippetProps: DNATextProps = {
    status: 'subtle',
    numberOfLines: 3,
    testID: 'drop-down-results-snippet',
  }

  return (
    <DNACard
      appearance="meetings"
      interactive={isInteractive ? 'pressable' : undefined}
      onPress={() => {
        onPress?.(documentORM)
      }}
      testID="meeting-document-card-container"
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
        styles.containerStyle,
        !isInteractive && { opacity: 0.4 },
      ]}
    >
      <DNAPopover disablePopover={!isDisabledHTMLorWebDocument} placement="top-start">
        <DNAPopover.Anchor>
          <DNABox alignY="center" spacing="between" childFill={0}>
            <DNABox alignY="center" childFill={1}>
              <DNABox appearance="row">
                <DNADocumentThumbnail.Hover
                  documentVersionORM={
                    documentORM.relations.version.latestPublishedDocumentVersionORM ||
                    documentORM.relations.version.latestDocumentVersionORM
                  }
                  popover={{
                    placement: 'left-start',
                  }}
                  unavailableContent={unavailableContent}
                  width={isSearch ? 160 : 120}
                  height={isSearch ? 90 : 68}
                  variant="meeting"
                />
              </DNABox>
              {/* Title */}
              <DNABox fill childFill appearance="col" spacing="sm" style={styles.titleText}>
                {snippet?.title
                  ? <RenderExcerpt
                      textProps={titleProps}
                      text={snippet.title.text}
                  />
                  : <DNAText {...titleProps}>
                    {documentORM.relations.version.latestUsableDocumentVersionORM.model.title}</DNAText>}
                {showSnippet && (
                  <RenderExcerpt
                    pageNumber={snippet.pageNumber}
                    textProps={snippetProps}
                    text={snippet.documentExcerpt.text}
                  />
                )}
                <DNABox alignX="start" alignY="start">
                  {!isPersonalDocument &&
                    <DNABox alignY="center" spacing="sm" fill>
                      <DNACustomFieldChipList documentVersionORM={latestUsableDocumentVersionORM} />
                      <DNAFileTypeChip orm={documentORM}/>
                      <Iffy is={unavailableContent}>
                        <DNAChip.Beacon
                          testID={`content-not-available-${title}`}
                          variant="CONTENT_UNAVAILABLE"
                        />
                      </Iffy>
                    </DNABox>}
                </DNABox>
              </DNABox>
            </DNABox>
            {/* Actions */}
            <DNABox spacing="sm" alignY="center">
              <Iffy is={isPersonalDocument}>
                <DNAText style={{ color: colors['color-text-secondary'], marginRight: 57 }}>
                  {format(new Date(documentORM.model.createdAt), 'MM/dd/yyyy')}
                </DNAText>
              </Iffy>
            </DNABox>
          </DNABox>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText
            testID="web-html-popup"
            colorMode="dark"
          >This type of document cannot be used in Presenter view</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNACard>
  )
}

export default DNADocumentMeetingRow
